import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
  ChevronRight,
  BookOpen,
  Code,
  Target,
  Brain,
  Star,
  Users,
  Clock,
  Zap,
  CheckCircle,
  Terminal,
  Bookmark,
  BookmarkCheck,
  Layers,
  Eye,
  EyeOff,
  Copy,
  Check,
  Monitor,
  Server
} from 'lucide-react';

// Import components
import ParkingLotSimulator from './components/ParkingLotSimulator';
import CodeEditor from './components/CodeEditor';
import LLDQuiz from './components/LLDQuiz';

const ParkingLotLLD: React.FC = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState('javascript');
  const [showSimulator, setShowSimulator] = useState(true);
  const [showCodeEditor, setShowCodeEditor] = useState(true);
  const [bookmarkedSections, setBookmarkedSections] = useState<string[]>([]);
  const [progress, setProgress] = useState<Record<string, boolean>>({});
  const [showQuiz, setShowQuiz] = useState(false);

  const sections = [
    { id: 'overview', label: 'Problem Overview', icon: BookOpen, difficulty: 'Beginner' },
    { id: 'requirements', label: 'Requirements Analysis', icon: Target, difficulty: 'Beginner' },
    { id: 'classes', label: 'Class Design', icon: Layers, difficulty: 'Intermediate' },
    { id: 'patterns', label: 'Design Patterns', icon: Brain, difficulty: 'Intermediate' },
    { id: 'implementation', label: 'Implementation', icon: Code, difficulty: 'Advanced' },
    { id: 'optimization', label: 'Optimization', icon: Zap, difficulty: 'Advanced' },
    { id: 'scaling', label: 'Scaling & Distribution', icon: Server, difficulty: 'Expert' },
    { id: 'testing', label: 'Testing Strategy', icon: CheckCircle, difficulty: 'Advanced' }
  ];

  const languages = [
    { id: 'javascript', label: 'JavaScript', icon: '🟨', color: 'yellow' },
    { id: 'typescript', label: 'TypeScript', icon: '🔷', color: 'blue' },
    { id: 'python', label: 'Python', icon: '🐍', color: 'green' },
    { id: 'java', label: 'Java', icon: '☕', color: 'orange' },
    { id: 'cpp', label: 'C++', icon: '⚡', color: 'purple' }
  ];

  const copyToClipboard = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const toggleBookmark = (sectionId: string) => {
    setBookmarkedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const markSectionComplete = (sectionId: string) => {
    setProgress(prev => ({ ...prev, [sectionId]: true }));
  };

  const renderCodeBlock = (code: string, title: string, description: string, id: string, language: string = 'javascript') => (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden mb-6">
      <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">{description}</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => copyToClipboard(code, id)}
              className="flex items-center space-x-2 px-3 py-1 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-md transition-colors"
            >
              {copiedCode === id ? (
                <>
                  <Check className="w-4 h-4" />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span>Copy</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      
      <div className="relative">
        <SyntaxHighlighter
          language={language}
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            borderRadius: '0 0 0.5rem 0.5rem',
            fontSize: '0.875rem',
            lineHeight: '1.5'
          }}
          showLineNumbers={false}
          wrapLines={true}
        >
          {code}
        </SyntaxHighlighter>
      </div>
    </div>
  );

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="space-y-8">
            <div className="prose prose-lg max-w-none dark:prose-invert">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Parking Lot System Design</h2>
              
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
                Design a comprehensive parking lot management system that can handle multiple vehicle types, 
                levels, and real-time operations. This is one of the most popular Low-Level Design interview questions 
                asked by companies like Amazon, Google, Microsoft, and Uber.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Problem Statement</h3>
              <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 mb-8">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  Design a parking lot system that can accommodate different types of vehicles. The parking lot should have multiple levels, 
                  and each level should have a certain number of spots reserved for different types of vehicles. The system should be able to 
                  tell a customer if a parking spot is available for their vehicle, assign a spot, calculate fees, and process payments.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Key Requirements</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-8">
                <li>Support multiple vehicle types: Motorcycle, Car, Truck</li>
                <li>Multiple parking levels with configurable spots</li>
                <li>Real-time availability tracking</li>
                <li>Time-based parking fee calculation</li>
                <li>Thread-safe concurrent operations</li>
                <li>Scalable architecture for large parking lots</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Learning Objectives</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-8">
                <li>Master object-oriented design principles (Encapsulation, Inheritance, Polymorphism)</li>
                <li>Apply appropriate design patterns (Factory, Observer, Strategy, Singleton)</li>
                <li>Handle complex business requirements and edge cases</li>
                <li>Design for scalability and extensibility</li>
                <li>Implement thread-safe operations for concurrent access</li>
                <li>Optimize for performance using efficient data structures</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Why This Problem is Important</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
                This problem is excellent for testing Low-Level Design skills because it:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-8">
                <li>Involves multiple interacting objects with clear responsibilities</li>
                <li>Requires understanding of object relationships and inheritance</li>
                <li>Tests knowledge of design patterns and their appropriate usage</li>
                <li>Challenges thinking about scalability and performance</li>
                <li>Covers real-world scenarios like concurrency and edge cases</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">What You'll Build</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Throughout this tutorial, you'll develop:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                <li>Complete class hierarchy with proper inheritance and polymorphism</li>
                <li>Working implementation in multiple programming languages</li>
                <li>Interactive visual simulator to understand system behavior</li>
                <li>Performance optimizations using efficient data structures</li>
                <li>Extensions for advanced features like reserved spots and dynamic pricing</li>
              </ul>
            </div>
          </div>
        );

      case 'requirements':
        return (
          <div className="space-y-8">
            <div className="prose prose-lg max-w-none dark:prose-invert">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Requirements Analysis</h2>
              
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
                Before diving into the design, we need to understand the complete set of requirements. 
                This step is crucial for identifying the scope and constraints of our system.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Functional Requirements</h3>
              
              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Core Features</h4>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li>Park and remove vehicles with automatic spot assignment</li>
                <li>Support multiple vehicle types: Motorcycle, Car, Truck</li>
                <li>Multiple parking levels with configurable spot layouts</li>
                <li>Real-time availability tracking and status updates</li>
                <li>Payment processing with time-based fee calculation</li>
                <li>Ticket generation and validation system</li>
              </ul>

              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Advanced Features</h4>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-8">
                <li>Reserved spots for handicapped, VIP, and electric vehicles</li>
                <li>Dynamic pricing based on time, demand, and spot type</li>
                <li>Multiple entry and exit points with automated gates</li>
                <li>Integration with mobile apps and payment systems</li>
                <li>Administrative dashboard for monitoring and analytics</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Non-Functional Requirements</h3>
              
              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Performance</h4>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li>Support 1000+ concurrent users</li>
                <li>Response time &lt;100ms for spot queries</li>
                <li>99.9% system uptime</li>
                <li>Handle 10,000+ vehicles per day</li>
              </ul>

              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Scalability</h4>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li>Support multiple parking lots across locations</li>
                <li>Horizontal scaling for increased load</li>
                <li>Database partitioning for large datasets</li>
                <li>Caching strategies for frequent operations</li>
              </ul>

              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Security & Reliability</h4>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-8">
                <li>Secure payment processing with encryption</li>
                <li>User authentication and authorization</li>
                <li>Audit logging for all transactions</li>
                <li>Data backup and disaster recovery</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">System Constraints</h3>
              
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-8">
                <li>Maximum 10 levels per parking lot</li>
                <li>Maximum 1000 spots per level</li>
                <li>Three vehicle types: Motorcycle (1 unit), Car (2 units), Truck (4 units)</li>
                <li>Payment must be completed within 15 minutes of exit</li>
                <li>Thread-safe operations for concurrent access</li>
                <li>Real-time updates with eventual consistency</li>
              </ul>
            </div>

            {renderCodeBlock(
              `// Requirements Specification in Code Comments

/*
 * PARKING LOT SYSTEM REQUIREMENTS
 * 
 * FUNCTIONAL REQUIREMENTS:
 * 1. Vehicle Management:
 *    - Support motorcycle, car, truck
 *    - Each vehicle has license plate, type, entry time
 * 
 * 2. Spot Management:
 *    - Different spot sizes for different vehicles
 *    - Motorcycle: 1 unit, Car: 2 units, Truck: 4 units
 *    - Track occupied/available status
 * 
 * 3. Level Management:
 *    - Multiple levels in parking lot
 *    - Each level has configurable spots
 *    - Level-wise vehicle tracking
 * 
 * 4. Parking Operations:
 *    - Find available spot for vehicle type
 *    - Park vehicle and generate ticket
 *    - Remove vehicle and calculate fee
 * 
 * 5. Payment System:
 *    - Time-based pricing model
 *    - Different rates for different vehicle types
 *    - Payment processing and receipt generation
 * 
 * NON-FUNCTIONAL REQUIREMENTS:
 * - Thread-safe operations for concurrent access
 * - O(1) spot finding for most common scenarios
 * - Extensible design for future enhancements
 * - Memory efficient for large parking lots
 */

// Example usage specification
class ParkingLotUsageContract {
  // Basic operations that must be supported
  parkVehicle(vehicle: Vehicle): ParkingTicket | null;
  removeVehicle(ticket: ParkingTicket): Payment;
  isSpotAvailable(vehicleType: VehicleType): boolean;
  getAvailableSpotCount(vehicleType: VehicleType): number;
}`,
              'Requirements Specification',
              'Detailed requirements translated into code comments and contracts',
              'requirements-spec'
            )}
          </div>
        );

      case 'classes':
        return (
          <div className="space-y-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">🏗️ Class Design & Architecture</h2>
            
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-6 rounded-lg border border-purple-200 dark:border-purple-700">
              <h3 className="text-xl font-bold text-purple-800 dark:text-purple-300 mb-4">🎯 Design Approach</h3>
              <p className="text-purple-700 dark:text-purple-400 mb-4">
                We'll follow object-oriented design principles to create a maintainable and extensible system. 
                The design focuses on clear separation of concerns, encapsulation, and loose coupling.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-purple-200 dark:border-purple-600">
                  <h4 className="font-semibold text-purple-700 dark:text-purple-400 mb-2">Entities</h4>
                  <p className="text-sm text-purple-600 dark:text-purple-500">Core business objects</p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-purple-200 dark:border-purple-600">
                  <h4 className="font-semibold text-purple-700 dark:text-purple-400 mb-2">Controllers</h4>
                  <p className="text-sm text-purple-600 dark:text-purple-500">Business logic handlers</p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-purple-200 dark:border-purple-600">
                  <h4 className="font-semibold text-purple-700 dark:text-purple-400 mb-2">Services</h4>
                  <p className="text-sm text-purple-600 dark:text-purple-500">External integrations</p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-purple-200 dark:border-purple-600">
                  <h4 className="font-semibold text-purple-700 dark:text-purple-400 mb-2">Utilities</h4>
                  <p className="text-sm text-purple-600 dark:text-purple-500">Helper classes</p>
                </div>
              </div>
            </div>

            {/* Core Entities */}
            <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg border border-blue-200 dark:border-blue-700">
              <h3 className="text-xl font-bold text-blue-800 dark:text-blue-300 mb-4">🏛️ Core Entities</h3>
              
              {renderCodeBlock(
                `// Vehicle hierarchy using inheritance and polymorphism
abstract class Vehicle {
  protected licensePlate: string;
  protected vehicleType: VehicleType;
  protected entryTime: Date;

  constructor(licensePlate: string, vehicleType: VehicleType) {
    this.licensePlate = licensePlate;
    this.vehicleType = vehicleType;
    this.entryTime = new Date();
  }

  abstract getSpotSize(): number;
  abstract getParkingFee(hours: number): number;

  // Getters
  getLicensePlate(): string { return this.licensePlate; }
  getVehicleType(): VehicleType { return this.vehicleType; }
  getEntryTime(): Date { return this.entryTime; }
}

// Concrete vehicle implementations
class Motorcycle extends Vehicle {
  constructor(licensePlate: string) {
    super(licensePlate, VehicleType.MOTORCYCLE);
  }

  getSpotSize(): number {
    return 1; // Takes 1 unit of space
  }

  getParkingFee(hours: number): number {
    return hours * 5; // $5 per hour
  }
}

class Car extends Vehicle {
  constructor(licensePlate: string) {
    super(licensePlate, VehicleType.CAR);
  }

  getSpotSize(): number {
    return 2; // Takes 2 units of space
  }

  getParkingFee(hours: number): number {
    return hours * 10; // $10 per hour
  }
}

class Truck extends Vehicle {
  constructor(licensePlate: string) {
    super(licensePlate, VehicleType.TRUCK);
  }

  getSpotSize(): number {
    return 4; // Takes 4 units of space
  }

  getParkingFee(hours: number): number {
    return hours * 20; // $20 per hour
  }
}

// Enums for type safety
enum VehicleType {
  MOTORCYCLE = 'MOTORCYCLE',
  CAR = 'CAR',
  TRUCK = 'TRUCK'
}

enum SpotSize {
  SMALL = 1,    // Motorcycle
  MEDIUM = 2,   // Car
  LARGE = 4     // Truck
}`,
                'Vehicle Class Hierarchy',
                'Abstract base class with concrete implementations for different vehicle types',
                'vehicle-hierarchy',
                'typescript'
              )}

              {renderCodeBlock(
                `// Parking spot management
class ParkingSpot {
  private spotId: string;
  private spotSize: SpotSize;
  private isOccupied: boolean;
  private parkedVehicle: Vehicle | null;
  private level: number;
  private rowNumber: number;
  private spotNumber: number;

  constructor(spotId: string, spotSize: SpotSize, level: number, row: number, spot: number) {
    this.spotId = spotId;
    this.spotSize = spotSize;
    this.isOccupied = false;
    this.parkedVehicle = null;
    this.level = level;
    this.rowNumber = row;
    this.spotNumber = spot;
  }

  // Check if vehicle can park in this spot
  canFitVehicle(vehicle: Vehicle): boolean {
    return !this.isOccupied && vehicle.getSpotSize() <= this.spotSize;
  }

  // Park vehicle in this spot
  parkVehicle(vehicle: Vehicle): boolean {
    if (!this.canFitVehicle(vehicle)) {
      return false;
    }
    
    this.isOccupied = true;
    this.parkedVehicle = vehicle;
    return true;
  }

  // Remove vehicle from spot
  removeVehicle(): Vehicle | null {
    if (!this.isOccupied) {
      return null;
    }

    const vehicle = this.parkedVehicle;
    this.isOccupied = false;
    this.parkedVehicle = null;
    return vehicle;
  }

  // Getters
  getSpotId(): string { return this.spotId; }
  getSpotSize(): SpotSize { return this.spotSize; }
  isAvailable(): boolean { return !this.isOccupied; }
  getParkedVehicle(): Vehicle | null { return this.parkedVehicle; }
  getLevel(): number { return this.level; }
  getLocation(): string { 
    return \`Level \${this.level}, Row \${this.rowNumber}, Spot \${this.spotNumber}\`;
  }
}`,
                'ParkingSpot Class',
                'Individual parking spot with vehicle management capabilities',
                'parking-spot',
                'typescript'
              )}
            </div>

            {/* Level and Parking Lot */}
            <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-lg border border-green-200 dark:border-green-700">
              <h3 className="text-xl font-bold text-green-800 dark:text-green-300 mb-4">🏢 Level & Parking Lot Structure</h3>
              
              {renderCodeBlock(
                `// Level management with efficient spot allocation
class Level {
  private levelNumber: number;
  private spots: Map<string, ParkingSpot>;
  private availableSpots: Map<SpotSize, Set<string>>;
  private occupiedSpots: Set<string>;

  constructor(levelNumber: number) {
    this.levelNumber = levelNumber;
    this.spots = new Map();
    this.availableSpots = new Map([
      [SpotSize.SMALL, new Set()],
      [SpotSize.MEDIUM, new Set()],
      [SpotSize.LARGE, new Set()]
    ]);
    this.occupiedSpots = new Set();
  }

  // Add spots to the level
  addSpot(spot: ParkingSpot): void {
    this.spots.set(spot.getSpotId(), spot);
    this.availableSpots.get(spot.getSpotSize())?.add(spot.getSpotId());
  }

  // Find available spot for vehicle type (O(1) average case)
  findAvailableSpot(vehicle: Vehicle): ParkingSpot | null {
    const requiredSize = vehicle.getSpotSize();
    
    // Check spots that can accommodate this vehicle (same size or larger)
    for (const [spotSize, spotIds] of this.availableSpots) {
      if (spotSize >= requiredSize && spotIds.size > 0) {
        const spotId = spotIds.values().next().value;
        return this.spots.get(spotId) || null;
      }
    }
    
    return null;
  }

  // Park vehicle in this level
  parkVehicle(vehicle: Vehicle): ParkingSpot | null {
    const spot = this.findAvailableSpot(vehicle);
    if (!spot) return null;

    if (spot.parkVehicle(vehicle)) {
      // Update availability tracking
      this.availableSpots.get(spot.getSpotSize())?.delete(spot.getSpotId());
      this.occupiedSpots.add(spot.getSpotId());
      return spot;
    }
    
    return null;
  }

  // Remove vehicle from level
  removeVehicle(spotId: string): Vehicle | null {
    const spot = this.spots.get(spotId);
    if (!spot) return null;

    const vehicle = spot.removeVehicle();
    if (vehicle) {
      // Update availability tracking
      this.occupiedSpots.delete(spotId);
      this.availableSpots.get(spot.getSpotSize())?.add(spotId);
    }
    
    return vehicle;
  }

  // Get availability statistics
  getAvailabilityStats(): {[key: string]: number} {
    return {
      motorcycleSpots: this.availableSpots.get(SpotSize.SMALL)?.size || 0,
      carSpots: this.availableSpots.get(SpotSize.MEDIUM)?.size || 0,
      truckSpots: this.availableSpots.get(SpotSize.LARGE)?.size || 0,
      totalAvailable: Array.from(this.availableSpots.values())
        .reduce((sum, set) => sum + set.size, 0),
      totalOccupied: this.occupiedSpots.size
    };
  }

  getLevelNumber(): number { return this.levelNumber; }
}`,
                'Level Management Class',
                'Efficient level management with O(1) spot finding using data structures',
                'level-class',
                'typescript'
              )}

              {renderCodeBlock(
                `// Main parking lot orchestrator
class ParkingLot {
  private name: string;
  private address: string;
  private levels: Map<number, Level>;
  private tickets: Map<string, ParkingTicket>;
  private ticketCounter: number;

  constructor(name: string, address: string) {
    this.name = name;
    this.address = address;
    this.levels = new Map();
    this.tickets = new Map();
    this.ticketCounter = 1;
  }

  // Add level to parking lot
  addLevel(level: Level): void {
    this.levels.set(level.getLevelNumber(), level);
  }

  // Park vehicle (find best available spot)
  parkVehicle(vehicle: Vehicle): ParkingTicket | null {
    // Try to park in levels sequentially
    for (const [levelNum, level] of this.levels) {
      const spot = level.parkVehicle(vehicle);
      if (spot) {
        // Generate parking ticket
        const ticket = new ParkingTicket(
          this.generateTicketId(),
          vehicle,
          spot,
          new Date()
        );
        
        this.tickets.set(ticket.getTicketId(), ticket);
        return ticket;
      }
    }
    
    return null; // No available spots
  }

  // Remove vehicle and calculate payment
  removeVehicle(ticketId: string): Payment | null {
    const ticket = this.tickets.get(ticketId);
    if (!ticket) return null;

    const level = this.levels.get(ticket.getSpot().getLevel());
    if (!level) return null;

    const vehicle = level.removeVehicle(ticket.getSpot().getSpotId());
    if (!vehicle) return null;

    // Calculate payment
    const exitTime = new Date();
    const payment = this.calculatePayment(ticket, exitTime);
    
    // Remove ticket from active tickets
    this.tickets.delete(ticketId);
    
    return payment;
  }

  // Calculate parking fee
  private calculatePayment(ticket: ParkingTicket, exitTime: Date): Payment {
    const entryTime = ticket.getEntryTime();
    const durationMs = exitTime.getTime() - entryTime.getTime();
    const hours = Math.ceil(durationMs / (1000 * 60 * 60)); // Round up to nearest hour
    
    const vehicle = ticket.getVehicle();
    const amount = vehicle.getParkingFee(hours);
    
    return new Payment(
      this.generatePaymentId(),
      ticket.getTicketId(),
      amount,
      hours,
      exitTime
    );
  }

  // Get overall availability
  getTotalAvailability(): {[key: string]: number} {
    const total = {
      motorcycleSpots: 0,
      carSpots: 0,
      truckSpots: 0,
      totalAvailable: 0,
      totalOccupied: 0,
      totalSpots: 0
    };

    for (const level of this.levels.values()) {
      const stats = level.getAvailabilityStats();
      total.motorcycleSpots += stats.motorcycleSpots;
      total.carSpots += stats.carSpots;
      total.truckSpots += stats.truckSpots;
      total.totalAvailable += stats.totalAvailable;
      total.totalOccupied += stats.totalOccupied;
    }

    total.totalSpots = total.totalAvailable + total.totalOccupied;
    return total;
  }

  private generateTicketId(): string {
    return \`TKT-\${String(this.ticketCounter++).padStart(6, '0')}\`;
  }

  private generatePaymentId(): string {
    return \`PAY-\${Date.now()}-\${Math.random().toString(36).substr(2, 4).toUpperCase()}\`;
  }

  // Getters
  getName(): string { return this.name; }
  getAddress(): string { return this.address; }
}`,
                'ParkingLot Main Class',
                'Core parking lot management with ticket generation and payment processing',
                'parking-lot-main',
                'typescript'
              )}
            </div>
          </div>
        );

      case 'patterns':
        return (
          <div className="space-y-8">
            <div className="prose prose-lg max-w-none dark:prose-invert">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Design Patterns</h2>
              
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
                Our parking lot system leverages several design patterns to ensure maintainability, extensibility, 
                and adherence to SOLID principles. Let's explore each pattern and its implementation.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">1. Singleton Pattern</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Ensures only one instance of the ParkingLot exists throughout the application lifecycle.
              </p>
              
              {renderCodeBlock(
                `class ParkingLot {
    private static instance: ParkingLot;
    private levels: Level[] = [];
    private totalSpots: number = 0;
    
    private constructor() {
        // Private constructor prevents direct instantiation
    }
    
    public static getInstance(): ParkingLot {
        if (!ParkingLot.instance) {
            ParkingLot.instance = new ParkingLot();
        }
        return ParkingLot.instance;
    }
    
    public addLevel(level: Level): void {
        this.levels.push(level);
        this.totalSpots += level.getTotalSpots();
    }
    
    public parkVehicle(vehicle: Vehicle): ParkingTicket | null {
        for (const level of this.levels) {
            const spot = level.findAvailableSpot(vehicle.getSize());
            if (spot) {
                spot.parkVehicle(vehicle);
                return new ParkingTicket(vehicle, spot, new Date());
            }
        }
        return null; // No available spot
    }
}`,
                "Singleton Pattern Implementation",
                "Ensures global access to a single parking lot instance",
                "singleton-pattern",
                "typescript"
              )}

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">2. Factory Pattern</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Creates different types of vehicles and parking spots without exposing creation logic.
              </p>

              {renderCodeBlock(
                `// Abstract Vehicle Factory
abstract class VehicleFactory {
    public abstract createVehicle(licensePlate: string): Vehicle;
}

// Concrete Factories
class MotorcycleFactory extends VehicleFactory {
    public createVehicle(licensePlate: string): Motorcycle {
        return new Motorcycle(licensePlate);
    }
}

class CarFactory extends VehicleFactory {
    public createVehicle(licensePlate: string): Car {
        return new Car(licensePlate);
    }
}

class TruckFactory extends VehicleFactory {
    public createVehicle(licensePlate: string): Truck {
        return new Truck(licensePlate);
    }
}

// Factory Provider
class VehicleFactoryProvider {
    public static getFactory(type: VehicleType): VehicleFactory {
        switch (type) {
            case VehicleType.MOTORCYCLE:
                return new MotorcycleFactory();
            case VehicleType.CAR:
                return new CarFactory();
            case VehicleType.TRUCK:
                return new TruckFactory();
            default:
                throw new Error("Invalid vehicle type");
        }
    }
}`,
                "Factory Pattern Implementation",
                "Creates vehicles and spots dynamically based on type",
                "factory-pattern",
                "typescript"
              )}

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">3. Observer Pattern</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Notifies interested parties when parking spot status changes.
              </p>

              {renderCodeBlock(
                `// Observer Interface
interface ParkingObserver {
    update(spot: ParkingSpot, event: ParkingEvent): void;
}

// Concrete Observers
class DisplayBoard implements ParkingObserver {
    update(spot: ParkingSpot, event: ParkingEvent): void {
        console.log(\`Display: Level \${spot.getLevel()} - \${event} at spot \${spot.getId()}\`);
        this.updateAvailabilityDisplay();
    }
    
    private updateAvailabilityDisplay(): void {
        // Update digital display boards
    }
}

class NotificationService implements ParkingObserver {
    update(spot: ParkingSpot, event: ParkingEvent): void {
        if (event === ParkingEvent.SPOT_FREED) {
            this.notifyWaitingCustomers(spot);
        }
    }
    
    private notifyWaitingCustomers(spot: ParkingSpot): void {
        // Send notifications to waiting customers
    }
}

// Subject (Observable)
class ParkingSpot {
    private observers: ParkingObserver[] = [];
    private isOccupied: boolean = false;
    
    public addObserver(observer: ParkingObserver): void {
        this.observers.push(observer);
    }
    
    public removeObserver(observer: ParkingObserver): void {
        const index = this.observers.indexOf(observer);
        if (index > -1) {
            this.observers.splice(index, 1);
        }
    }
    
    private notifyObservers(event: ParkingEvent): void {
        for (const observer of this.observers) {
            observer.update(this, event);
        }
    }
    
    public parkVehicle(vehicle: Vehicle): boolean {
        if (!this.isOccupied && this.canFitVehicle(vehicle)) {
            this.isOccupied = true;
            this.vehicle = vehicle;
            this.notifyObservers(ParkingEvent.SPOT_OCCUPIED);
            return true;
        }
        return false;
    }
    
    public removeVehicle(): void {
        if (this.isOccupied) {
            this.isOccupied = false;
            this.vehicle = null;
            this.notifyObservers(ParkingEvent.SPOT_FREED);
        }
    }
}`,
                "Observer Pattern Implementation",
                "Real-time notifications for parking status changes",
                "observer-pattern",
                "typescript"
              )}

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">4. Strategy Pattern</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Implements different pricing strategies for flexible fee calculation.
              </p>

              {renderCodeBlock(
                `// Strategy Interface
interface PricingStrategy {
    calculateFee(vehicle: Vehicle, parkingDuration: number): number;
}

// Concrete Strategies
class HourlyPricingStrategy implements PricingStrategy {
    private readonly hourlyRates = new Map([
        [VehicleType.MOTORCYCLE, 2],
        [VehicleType.CAR, 5],
        [VehicleType.TRUCK, 10]
    ]);
    
    calculateFee(vehicle: Vehicle, parkingDuration: number): number {
        const hourlyRate = this.hourlyRates.get(vehicle.getType()) || 5;
        const hours = Math.ceil(parkingDuration / (1000 * 60 * 60));
        return hours * hourlyRate;
    }
}

class PeakHourPricingStrategy implements PricingStrategy {
    private readonly peakMultiplier = 1.5;
    private readonly basePricing = new HourlyPricingStrategy();
    
    calculateFee(vehicle: Vehicle, parkingDuration: number): number {
        const baseFee = this.basePricing.calculateFee(vehicle, parkingDuration);
        return this.isPeakHour() ? baseFee * this.peakMultiplier : baseFee;
    }
    
    private isPeakHour(): boolean {
        const hour = new Date().getHours();
        return (hour >= 7 && hour <= 9) || (hour >= 17 && hour <= 19);
    }
}

// Context
class ParkingTicket {
    private pricingStrategy: PricingStrategy;
    
    constructor(private vehicle: Vehicle, private spot: ParkingSpot, 
                private entryTime: Date, strategy?: PricingStrategy) {
        this.pricingStrategy = strategy || new HourlyPricingStrategy();
    }
    
    public calculateFee(): number {
        const duration = Date.now() - this.entryTime.getTime();
        return this.pricingStrategy.calculateFee(this.vehicle, duration);
    }
    
    public setPricingStrategy(strategy: PricingStrategy): void {
        this.pricingStrategy = strategy;
    }
}`,
                "Strategy Pattern Implementation",
                "Flexible pricing strategies for different scenarios",
                "strategy-pattern",
                "typescript"
              )}
            </div>
          </div>
        );

      case 'implementation':
        return (
          <div className="space-y-8">
            <div className="prose prose-lg max-w-none dark:prose-invert">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Complete Implementation</h2>
              
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
                Here's the complete implementation of our parking lot system, bringing together all the 
                design patterns and principles we've discussed.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Core Entities</h3>

              {renderCodeBlock(
                `// Base Vehicle Class
abstract class Vehicle {
    constructor(
        protected licensePlate: string,
        protected type: VehicleType
    ) {}
    
    public getLicensePlate(): string {
        return this.licensePlate;
    }
    
    public getType(): VehicleType {
        return this.type;
    }
    
    public abstract getSize(): number;
}

// Concrete Vehicle Classes
class Motorcycle extends Vehicle {
    constructor(licensePlate: string) {
        super(licensePlate, VehicleType.MOTORCYCLE);
    }
    
    public getSize(): number {
        return 1;
    }
}

class Car extends Vehicle {
    constructor(licensePlate: string) {
        super(licensePlate, VehicleType.CAR);
    }
    
    public getSize(): number {
        return 2;
    }
}

class Truck extends Vehicle {
    constructor(licensePlate: string) {
        super(licensePlate, VehicleType.TRUCK);
    }
    
    public getSize(): number {
        return 4;
    }
}

// Enums and Types
enum VehicleType {
    MOTORCYCLE = "MOTORCYCLE",
    CAR = "CAR",
    TRUCK = "TRUCK"
}

enum ParkingSpotType {
    MOTORCYCLE = "MOTORCYCLE",
    COMPACT = "COMPACT",
    LARGE = "LARGE",
    HANDICAPPED = "HANDICAPPED",
    VIP = "VIP"
}

enum ParkingEvent {
    SPOT_OCCUPIED = "SPOT_OCCUPIED",
    SPOT_FREED = "SPOT_FREED"
}`,
                "Core Entity Classes",
                "Base classes for vehicles and enums",
                "core-entities",
                "typescript"
              )}

              {renderCodeBlock(
                `// Usage Example
const parkingLot = ParkingLot.getInstance();

// Create levels
const level1 = new Level(1);
const level2 = new Level(2);

// Add spots to level 1
for (let i = 1; i <= 20; i++) {
    const spotType = i <= 5 ? ParkingSpotType.MOTORCYCLE : 
                    i <= 15 ? ParkingSpotType.COMPACT : 
                    ParkingSpotType.LARGE;
    const size = i <= 5 ? 1 : i <= 15 ? 2 : 4;
    level1.addSpot(new ParkingSpot(\`L1-S\${i}\`, spotType, size, 1));
}

// Add levels to parking lot
parkingLot.addLevel(level1);
parkingLot.addLevel(level2);

// Park vehicles
const carFactory = VehicleFactoryProvider.getFactory(VehicleType.CAR);
const car = carFactory.createVehicle("ABC123");

const ticket = parkingLot.parkVehicle(car);
if (ticket) {
    console.log(\`Vehicle parked. Ticket ID: \${ticket.getId()}\`);
    
    // Later, when leaving
    const fee = parkingLot.unparkVehicle(ticket.getId());
    console.log(\`Parking fee: $\${fee}\`);
} else {
    console.log("No available parking spots");
}

// Check occupancy
console.log(\`Occupancy Rate: \${parkingLot.getOccupancyRate().toFixed(1)}%\`);
console.log(\`Available Spots: \${parkingLot.getAvailableSpots()}/\${parkingLot.getTotalSpots()}\`);`,
                "System Usage Example",
                "How to use the complete parking lot system",
                "usage-example",
                "typescript"
              )}
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-16">
            <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">
              Section Under Development
            </h3>
            <p className="text-gray-500 dark:text-gray-500">
              This section is being crafted with comprehensive content. Please check other sections!
            </p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 pt-16">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <nav className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
            <Link to="/practice" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              Practice
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/practice/lld" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              Low Level Design
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900 dark:text-gray-100">Parking Lot System</span>
          </nav>
          
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4 font-display">
                🅿️ Parking Lot System Design
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl">
                Master low-level design through a comprehensive parking lot system. Learn OOP principles, 
                design patterns, and system architecture through interactive coding and visual simulations.
              </p>
            </div>
            
            <div className="flex items-center space-x-4 mt-6 lg:mt-0">
              <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                <Star className="w-4 h-4 text-yellow-500" />
                <span>4.9</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                <Users className="w-4 h-4" />
                <span>2.1k learners</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                <Clock className="w-4 h-4" />
                <span>2-3 hours</span>
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Language:</span>
              <div className="flex space-x-1">
                {languages.map((lang) => (
                  <button
                    key={lang.id}
                    onClick={() => setCurrentLanguage(lang.id)}
                    className={`px-3 py-1 text-xs rounded-md border transition-all ${
                      currentLanguage === lang.id
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:border-blue-400'
                    }`}
                  >
                    <span className="mr-1">{lang.icon}</span>
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowSimulator(!showSimulator)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg border text-sm transition-all ${
                showSimulator
                  ? 'bg-green-600 text-white border-green-600'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600'
              }`}
            >
              {showSimulator ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              <span>Simulator</span>
            </button>
            
            <button
              onClick={() => setShowCodeEditor(!showCodeEditor)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg border text-sm transition-all ${
                showCodeEditor
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600'
              }`}
            >
              <Terminal className="w-4 h-4" />
              <span>Code Lab</span>
            </button>

            <button
              onClick={() => setShowQuiz(!showQuiz)}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm transition-colors"
            >
              <Brain className="w-4 h-4" />
              <span>Take Quiz</span>
            </button>
          </div>
        </div>

        {/* Main Content Layout */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
          {/* Left Sidebar - Navigation */}
          <div className="xl:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Progress */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  📊 Your Progress
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600 dark:text-gray-400">Overall</span>
                      <span className="text-gray-900 dark:text-gray-100">
                        {Math.round((Object.keys(progress).length / sections.length) * 100)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(Object.keys(progress).length / sections.length) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    {Object.keys(progress).length} of {sections.length} sections completed
                  </div>
                </div>
              </div>

              {/* Section Navigation */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  📑 Sections
                </h3>
                <nav className="space-y-2">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-all ${
                        activeSection === section.id
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-700'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <section.icon className={`w-5 h-5 ${
                          activeSection === section.id 
                            ? 'text-blue-600 dark:text-blue-400' 
                            : 'text-gray-400'
                        }`} />
                        <div>
                          <div className="font-medium">{section.label}</div>
                          <div className="text-xs opacity-75">{section.difficulty}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        {progress[section.id] && (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleBookmark(section.id);
                          }}
                          className="opacity-0 group-hover:opacity-100 hover:text-yellow-500 transition-all"
                        >
                          {bookmarkedSections.includes(section.id) ? (
                            <BookmarkCheck className="w-4 h-4 text-yellow-500" />
                          ) : (
                            <Bookmark className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className={`${showSimulator || showCodeEditor ? 'xl:col-span-2' : 'xl:col-span-3'}`}>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8">
              {showQuiz ? (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      🧠 LLD Knowledge Quiz
                    </h2>
                    <button
                      onClick={() => setShowQuiz(false)}
                      className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                    >
                      ← Back to Learning
                    </button>
                  </div>
                  <LLDQuiz />
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      {(() => {
                      const section = sections.find(s => s.id === activeSection);
                      const IconComponent = section?.icon || BookOpen;
                      return <IconComponent className="w-8 h-8 text-blue-600" />;
                    })()}
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                          {sections.find(s => s.id === activeSection)?.label}
                        </h2>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                          <span>{sections.find(s => s.id === activeSection)?.difficulty}</span>
                          <span>•</span>
                          <span>15-20 min read</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => toggleBookmark(activeSection)}
                        className={`p-2 rounded-lg transition-colors ${
                          bookmarkedSections.includes(activeSection)
                            ? 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/30'
                            : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                        }`}
                      >
                        {bookmarkedSections.includes(activeSection) ? (
                          <BookmarkCheck className="w-5 h-5" />
                        ) : (
                          <Bookmark className="w-5 h-5" />
                        )}
                      </button>
                      
                      {!progress[activeSection] && (
                        <button
                          onClick={() => markSectionComplete(activeSection)}
                          className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm transition-colors"
                        >
                          <CheckCircle className="w-4 h-4" />
                          <span>Mark Complete</span>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="prose prose-lg max-w-none dark:prose-invert">
                    {renderSectionContent()}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar - Interactive Tools */}
          {(showSimulator || showCodeEditor) && (
            <div className="xl:col-span-1">
              <div className="space-y-6">
                {showSimulator && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                      <Monitor className="w-5 h-5 mr-2 text-green-600" />
                      Interactive Simulator
                    </h3>
                    <div className="min-h-[600px]">
                      <ParkingLotSimulator />
                    </div>
                  </div>
                )}

                {showCodeEditor && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                      <Terminal className="w-5 h-5 mr-2 text-blue-600" />
                      Code Laboratory
                    </h3>
                    <div className="min-h-[600px]">
                      <CodeEditor />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParkingLotLLD;
